import { useState } from 'react'
import { useNavigate } from 'react-router'
import { ClockIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import type { ThreadRead } from '../client'
import RenameDialog from '../components/chat/RenameDialog'
import { useChat } from '../providers/chat/context'
import { formatDate, relativeTime } from '../lib/format'

export default function Conversations() {
  const { threads, openThread, archive, hasMoreThreads, loadingMoreThreads, loadMoreThreads } = useChat()
  const navigate = useNavigate()
  const [renaming, setRenaming] = useState<ThreadRead | null>(null)

  function open(threadId: string) {
    void openThread(threadId)
    void navigate(`/c/${threadId}`)
  }

  return (
    <div className="flex flex-1 flex-col">
      <header className="border-b border-stone-200 px-4 py-4 sm:px-6">
        <h1 className="text-base font-medium text-stone-900">Past conversations</h1>
      </header>

      <section className="px-4 py-5 sm:px-6">
        <p className="mb-2 text-sm text-stone-500">
          All conversations <span className="text-stone-400">({threads.length})</span>
        </p>

        {threads.length === 0 ? (
          <p className="py-10 text-center text-sm text-stone-400">No conversations yet.</p>
        ) : (
          <ul className="divide-y divide-stone-200 border-y border-stone-200">
            {threads.map((thread) => (
              <li key={thread.id} className="group flex items-center gap-4 py-3.5">
                <button
                  type="button"
                  onClick={() => open(thread.id)}
                  className="min-w-0 flex-1 text-left"
                >
                  <p className="truncate text-sm text-stone-900">{thread.title || 'New conversation'}</p>
                  <p className="mt-0.5 flex items-center gap-1 text-xs text-stone-500">
                    {thread.scheduled && (
                      <>
                        <ClockIcon className="size-3.5 text-purple-700" />
                        <span>Scheduled run ·</span>
                      </>
                    )}
                    <span>Updated {relativeTime(thread.updated_at)}</span>
                    <span className="text-stone-400"> · {formatDate(thread.updated_at)}</span>
                  </p>
                </button>

                <div className="flex shrink-0 items-center gap-1 opacity-0 transition group-hover:opacity-100 focus-within:opacity-100 pointer-coarse:opacity-100">
                  <button
                    type="button"
                    aria-label="Rename conversation"
                    onClick={() => setRenaming(thread)}
                    className="rounded-md p-1.5 text-stone-500 transition hover:bg-stone-200 hover:text-stone-900"
                  >
                    <PencilSquareIcon className="size-4" />
                  </button>
                  <button
                    type="button"
                    aria-label="Delete conversation"
                    onClick={() => void archive(thread.id)}
                    className="rounded-md p-1.5 text-stone-500 transition hover:bg-red-100 hover:text-red-600"
                  >
                    <TrashIcon className="size-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {hasMoreThreads && (
          <div className="flex justify-center pt-4">
            <button
              type="button"
              disabled={loadingMoreThreads}
              onClick={() => void loadMoreThreads()}
              className="rounded-md border border-stone-300 bg-stone-100 px-4 py-2 text-sm font-medium text-stone-700 transition hover:bg-stone-200 disabled:opacity-50"
            >
              {loadingMoreThreads ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </section>

      {renaming && <RenameDialog thread={renaming} onClose={() => setRenaming(null)} />}
    </div>
  )
}
